// 1. React
import { forwardRef, memo, useImperativeHandle, useRef } from "react";

// 2. Helpers
import { useTranslate } from "helpers";
import { useOpenOnTab } from "services/hooks/useOpenOnTab";

// 3. Types
import { VehicleContractCardProps, VehicleContractRef } from "./types";

// 4. UI Components
import { Tabs } from "ui/components/Tabs";

// 5. Local Components
import VehicleContractSaleCard from "./components/VehicleContractSaleCard/VehicleContractSaleCard";
import { VehicleContractSaleRef } from "./components/VehicleContractSaleCard/types";
import VehicleContractRentCard from "./components/VehicleContractRentCard/VehicleContractRentCard";
import { VehicleContractRentRef } from "./components/VehicleContractRentCard/types";
import VehicleContractStatsCard from "./components/VehicleContractStatsCard/VehicleContractStatsCard";

const VehicleContractCard = forwardRef<VehicleContractRef, VehicleContractCardProps>((props, ref) => {
  const { fleetVehicleDetail, onInvoiceCreated } = props;

  const { t } = useTranslate();
  const vehicleContractSaleRef = useRef<VehicleContractSaleRef>(null);
  const vehicleContractRentRef = useRef<VehicleContractRentRef>(null);

  const { activeTab, setActiveTab, openOnTab } = useOpenOnTab();

  useImperativeHandle(ref, () => ({
    openAddNewSale: () => openOnTab(0, () => vehicleContractSaleRef.current?.openAddNew()),
    openAddNewRent: () => openOnTab(1, () => vehicleContractRentRef.current?.openAddNew()),
    refreshSale: () => openOnTab(0, () => vehicleContractSaleRef.current?.refresh()),
    refreshRent: () => openOnTab(1, () => vehicleContractRentRef.current?.refresh()),
  }));

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <Tabs
        activeIndex={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          {
            label: t("admin.fleet.vehicle.contract.tab.sale"),
            content: (
              <div>
                <VehicleContractSaleCard ref={vehicleContractSaleRef} fleetVehicleDetail={fleetVehicleDetail} onInvoiceCreated={onInvoiceCreated} />
              </div>
            ),
          },
          {
            label: t("admin.fleet.vehicle.contract.tab.rent"),
            content: (
              <div>
                <VehicleContractRentCard ref={vehicleContractRentRef} fleetVehicleDetail={fleetVehicleDetail} onInvoiceCreated={onInvoiceCreated} />
              </div>
            ),
          },
          {
            label: t("admin.fleet.vehicle.contract.tab.stats"),
            content: (
              <div>
                <VehicleContractStatsCard fleetVehicleDetail={fleetVehicleDetail} />
              </div>
            ),
          },
        ]}
      />
    </>
  );
});

export default memo(VehicleContractCard);